/**
 * The Engagement deactivate confirmation (f7.1), driven entirely by the server.
 *
 * The prototype's `modal narrow` with `alert alert-warning` carrying `a-title`
 * and `a-body`, plus its Reason textarea -- the same shape f4.1 established
 * and f5.1 and f6.1 reused.
 *
 * **The client never computes the child list.** The sequence is f4.1's, and
 * this is the fourth feature to use it:
 *
 * 1. The dialog opens as a plain confirmation.
 * 2. Confirming issues the deactivation **without** `acknowledge_active_children`.
 * 3. If the server refuses with `409 ACTIVE_CHILDREN_PRESENT`, the dialog stays
 *    open and renders the elements **it** named.
 * 4. Confirming again re-issues the same request with the acknowledgement.
 *
 * At this level the children are the engagement's active **delivery
 * elements**, and they are never cascaded: they stay attached, keep their
 * status and keep their history. That is the same promise f6.1's dialog makes
 * one level up, and it is what makes "nothing is deleted" true rather than
 * merely claimed.
 *
 * The server caps the named children at 25 while reporting the real total.
 * That cap is sufficient here by measurement rather than by luck: the largest
 * element set in the estate is **28**, and the cap's purpose is to bound a
 * message rather than to page a list.
 */

import { useState } from 'react';
import { Ban, TriangleAlert } from 'lucide-react';

import type { Engagement } from '@/api/client';
import { useDeactivateEngagement } from '@/api/queries';
import { Modal } from '@/routes/reference-data/Modal';

import { activeChildrenFrom, generalMessage } from './engagementView';

export function DeactivateEngagementDialog({
  engagement,
  onClose,
}: {
  engagement: Engagement;
  onClose: () => void;
}) {
  const deactivate = useDeactivateEngagement();
  const [reason, setReason] = useState('');

  const activeChildren = activeChildrenFrom(deactivate.error);
  /*
   * A refusal that named children is the expected answer to step 2, not a
   * failure to repeat at the top of the dialog.
   */
  const failure = activeChildren ? null : generalMessage(deactivate.error);

  function submit() {
    deactivate.mutate(
      {
        engagementId: engagement.id,
        body: {
          // False on the first attempt, true once the warning has been seen.
          acknowledge_active_children: activeChildren !== null,
          ...(reason.trim() ? { reason: reason.trim() } : {}),
        },
      },
      { onSuccess: onClose },
    );
  }

  return (
    <Modal
      title="Deactivate engagement?"
      subtitle={`${engagement.code} · ${engagement.name}`}
      onClose={onClose}
      onSubmit={submit}
      busy={deactivate.isPending}
      submitLabel={activeChildren ? 'Deactivate anyway' : 'Deactivate'}
      submitIcon={<Ban aria-hidden="true" />}
      submitClassName="btn btn-danger"
      narrow
      layout="plain"
      message={failure}
    >
      <div className="alert alert-warning mb-4" role="alert">
        <TriangleAlert aria-hidden="true" />
        <div>
          <div className="a-title">
            {activeChildren && activeChildren.total > 0
              ? `${activeChildren.total} active delivery ${
                  activeChildren.total === 1 ? 'element' : 'elements'
                } below this engagement`
              : `${engagement.name} stops being offered for new records`}
          </div>
          <div className="a-body">
            They stay attached and keep their history. Nothing is deleted, no
            element is cascaded, and the engagement can be reactivated.
          </div>
          {activeChildren && activeChildren.listed.length > 0 && (
            <>
              <ul className="group-children">
                {activeChildren.listed.map((entry) => (
                  <li key={entry.id}>
                    <span className="mono">{entry.code}</span>
                    {entry.name === entry.code ? '' : ` ${entry.name}`}
                  </li>
                ))}
              </ul>
              {activeChildren.total > activeChildren.listed.length && (
                <div className="a-body">
                  and {activeChildren.total - activeChildren.listed.length} more.
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="field">
        <label htmlFor="eng-deact-reason">Reason</label>
        <textarea
          id="eng-deact-reason"
          value={reason}
          placeholder="Recorded on the audit entry"
          onChange={(event) => setReason(event.target.value)}
        />
      </div>
    </Modal>
  );
}
